import { type ReactNode, useCallback } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { useElementHeight } from '@repo/ui';

import { useDialog } from '@shared/hooks/useDialog';

const COLLAPSED_VISIBLE_HEIGHT = 25; // in px - height of dialog even when collapsed

interface Props {
  children: ReactNode;
  testId: TestId;
  contentClassName?: string;
  expandTrigger?: string;
  collapseTrigger?: string | number;
  onClose?: () => void;
  onHeightChange?: (height: number) => void;
  opened?: boolean;
  portalTo?: string;
  sticker?: ReactNode;
  stickerTop?: string;
  warning?: boolean;
}

export default function Dialog({
  children,
  testId,
  contentClassName = 'px-4',
  expandTrigger,
  collapseTrigger,
  onClose,
  onHeightChange,
  opened = true,
  portalTo,
  sticker,
  stickerTop,
  warning,
}: Props) {
  const { expanded, shouldRender, dialogRef, handleRef, handleClick } = useDialog({
    expandTrigger,
    collapseTrigger,
    onClose,
    opened,
  });

  const handleHeightChange = useCallback(
    (height: number) => {
      onHeightChange?.(expanded ? height : COLLAPSED_VISIBLE_HEIGHT);
    },
    [onHeightChange, expanded]
  );

  useElementHeight(dialogRef, onHeightChange ? handleHeightChange : undefined, shouldRender);

  if (!shouldRender) {
    return null;
  }

  const dialog = (
    <div
      ref={dialogRef}
      className={classNames(
        'bottom-0 left-0 w-full transition-transform duration-300 ease-in-out md:ml-19 md:w-150',
        portalTo ? 'z-modal fixed md:absolute' : 'absolute z-2',
        expanded ? 'translate-y-0' : 'translate-y-[calc(100%-25px)]'
      )}
      data-testid={testId}
    >
      {sticker && (
        <div className={classNames('absolute left-0 w-full text-center', stickerTop ?? 'top-0')}>{sticker}</div>
      )}
      <div
        className={classNames(
          'flex max-h-[90vh] flex-col overflow-hidden md:rounded-t-2xl',
          'drop-shadow-2xl drop-shadow-gray-950/50',
          warning ? 'bg-warning-soft' : 'bg-primary'
        )}
      >
        <button
          ref={handleRef}
          type="button"
          className="touch-action-none w-full shrink-0 cursor-pointer border-none bg-transparent pt-2 pb-4"
          onClick={handleClick}
          aria-label={expanded ? tr('drawer.collapse') : tr('drawer.expand')}
        >
          <div className="bg-quaternary-medium mx-auto h-1 w-16 rounded-4xl" />
        </button>

        <div className={classNames('min-h-0 flex-1 overflow-y-auto transition-opacity duration-300 ease-in-out')}>
          <div className={contentClassName}>{children}</div>
        </div>
      </div>
    </div>
  );

  if (portalTo) {
    const portalContainer = document.getElementById(portalTo);
    if (portalContainer) {
      return createPortal(dialog, portalContainer);
    }
  }

  return dialog;
}
